import React from 'react';
import { ExternalLink, Mail } from 'lucide-react';
import Button from './Button';
import { useTranslations } from '../context/TranslationContext';

interface ProductPageProps {
  eyebrow: string;
  title: string;
  description: string;
  children: React.ReactNode;
  primaryLabel?: string;
  primaryHref?: string;
  contactLabel: string;
  contactSubject: string;
}

const ProductPage: React.FC<ProductPageProps> = ({
  eyebrow, title, description, children, primaryLabel, primaryHref, contactLabel, contactSubject
}) => {
  const { t } = useTranslations();
  const contact = () => {
    const body = encodeURIComponent(t('product_contact_body'));
    window.location.href = `mailto:info@legisratio.it?subject=${encodeURIComponent(contactSubject)}&body=${body}`;
  };

  return (
    <main className="bg-void-blue text-white">
      <section className="relative overflow-hidden pt-36 pb-20 md:pt-44 md:pb-28">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-40 right-0 h-[520px] w-[520px] rounded-full bg-mystic-violet/15 blur-[130px]" />
          <div className="absolute bottom-0 left-1/4 h-[320px] w-[320px] rounded-full bg-cosmic-teal/10 blur-[110px]" />
        </div>
        <div className="relative mx-auto w-full max-w-7xl px-6 md:px-12">
          <p className="mb-5 text-sm font-bold uppercase tracking-[0.22em] text-cosmic-teal">{eyebrow}</p>
          <h1 className="max-w-5xl font-display text-4xl font-bold leading-tight md:text-6xl">{title}</h1>
          <p className="mt-7 max-w-3xl text-lg leading-relaxed text-gray-300 md:text-xl">{description}</p>
          <div className="mt-10 flex flex-col gap-4 sm:flex-row">
            {primaryLabel && primaryHref && (
              <Button href={primaryHref} target="_blank" rel="noopener noreferrer" variant="gradient" size="lg" icon={<ExternalLink size={20} />} className="w-full sm:w-auto">
                {primaryLabel}
              </Button>
            )}
            <Button variant={primaryLabel ? 'secondary' : 'gradient'} size="lg" icon={<Mail size={20} />} onClick={contact} className="w-full sm:w-auto">
              {contactLabel}
            </Button>
          </div>
        </div>
      </section>
      {children}
    </main>
  );
};

export const ProductSection: React.FC<{ title: string; intro?: string; children: React.ReactNode; className?: string }> = ({ title, intro, children, className = '' }) => (
  <section className={`border-t border-white/10 py-16 md:py-24 ${className}`}>
    <div className="mx-auto w-full max-w-7xl px-6 md:px-12">
      <div className="mb-10 max-w-3xl">
        <h2 className="font-display text-3xl font-bold md:text-4xl">{title}</h2>
        {intro && <p className="mt-5 text-lg leading-relaxed text-gray-300">{intro}</p>}
      </div>
      {children}
    </div>
  </section>
);

export default ProductPage;
